<template>
  <v-card class="ma-4">
    <v-toolbar>
      <v-toolbar-title>{{ eventTitle }}</v-toolbar-title>
      <span class="ml-4 mt-1">on {{ eventDate }}</span>
      <v-spacer />
      <v-btn to="/serve/super" outlined>Back to Event List</v-btn>
      <v-spacer />
      <v-dialog v-model="deleteDialog" :persistent="isDeleting" width="400">
        <template #activator="{ on }">
          <v-btn v-on="on" color="error" outlined>
            <v-icon left>fas fa-trash</v-icon>
            Delete Event
          </v-btn>
        </template>
        <v-card>
          <v-card-title>Delete Event</v-card-title>
          <v-card-text>
            <p v-if="commitedCompleteStudents > 0">There {{ commitedCompleteStudents === 1 ? 'is one student who is' : `are ${commitedCompleteStudents} students who are` }} marked as Committed or Completed. You cannot delete an event with students in this status. Go to the Students tab and move students out of these statuses before you can delete this event.</p>
            <p v-else-if="students.length > 0">Make sure you have communicated with students before deleting this event. Once the event is deleted, there will no longer be any record of this event or the students connected to it.</p>
            <p v-else>Click Confirm below to delete this event. There is no undoing this.</p>
          </v-card-text>
          <v-card-actions>
            <v-btn :disabled="commitedCompleteStudents !== 0 || isDeleting" color="error" text @click="deleteEvent()">Delete Event</v-btn>
            <v-spacer />
            <v-btn :disabled="isDeleting" @click="deleteDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-toolbar>
    <v-tabs v-model="tab">
      <v-tab>Event Info</v-tab>
      <v-tab>Students</v-tab>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item>
        <v-card-text>
          <v-row>
            <v-col :cols="12" md="6">
              <p>Event Description. This can include some of the duties students will be performing, or any other information that would be relevant for the students to see. You may leave this blank if nothing is necessary.</p>
              <html-editor v-model="description" label="Description" />
              <p class="mt-4">Please enter the date range for when students can sign up for this event.</p>
              <v-row>
                <v-col>
                  <date-picker v-model="regOpen" label="Sign-Up Open" outlined />
                </v-col>
                <v-col>
                  <date-picker v-model="regClosed" label="Sign-Up Closed" :max="eventDate" outlined />
                </v-col>
              </v-row>
            </v-col>
            <v-col :cols="12" md="6">
              <h3>Duties/Selectable Options</h3>
              <p>This is where you can specify the options within your event that someone can sign up to do. If you only have one option, just enter the one, and there's no limit to how many you can add here.</p>
              <v-row>
                <v-col>
                  <v-text-field v-model="blockSearch" label="Filter by Desc/Date" outlined hide-details />
                </v-col>
                <v-col>
                  <v-select v-model="blockFilter" :items="blockFilterItems" label="Filter by Status" outlined hide-details />
                </v-col>
              </v-row>
              <v-list>
                <v-list-item v-for="{ _id, desc, slotCount, slotsFilled, minHours, maxHours } of filteredBlocks" :key="_id">
                  <v-list-item-content>
                    <v-list-item-title>{{ desc }}</v-list-item-title>
                    <v-list-item-subtitle>Slots: {{ slotsFilled }} out of {{ slotCount }}</v-list-item-subtitle>
                    <v-list-item-subtitle v-if="minHours === maxHours">{{ minHours }} Hour{{ minHours === 1 ? '' : 's' }}</v-list-item-subtitle>
                    <v-list-item-subtitle v-else>{{ minHours }} to {{ maxHours }} Hours</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip color="#193264" style="color:white;">{{ slotCount - slotsFilled }} Left</v-chip>
                  </v-list-item-action>
                  <v-list-item-action>
                    <v-btn icon @click="editBlock(_id)">
                      <v-icon small>fal fa-pencil</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
              <v-btn @click="addBlock()">Add Option</v-btn>
            </v-col>
            <v-dialog v-model="blockDialog" width="400">
              <v-card>
                <v-toolbar class="mb-4">
                  <v-toolbar-title>{{ block._id ? 'Edit' : 'Add' }} Option</v-toolbar-title>
                  <v-spacer />
                  <v-btn color="error" @click="removeBlock()">
                    <v-icon left>fal fa-trash-alt</v-icon>
                    Remove
                  </v-btn>
                </v-toolbar>
                <v-card-text>
                  <p>Enter the description for this sign-up option. If there is a specific time for this, please make sure to include that.</p>
                  <v-text-field v-model="block.desc" label="Description" outlined hide-details />
                  <p>Enter how many student slots would be available for this option.</p>
                  <v-text-field v-model="block.slotCount" label="Slot Count" type="number" outlined hide-details />
                  <p>Enter the range of how many hours a student can sign up for when selecting this option. If it's a fixed number of hours, enter the same value for both min and max.</p>
                  <v-row>
                    <v-col>
                      <v-text-field v-model="block.minHours" label="Min Hours" type="number" outlined hide-details />
                    </v-col>
                    <v-col>
                      <v-text-field v-model="block.maxHours" label="Max Hours" type="number" outlined hide-details />
                    </v-col>
                  </v-row>
                  <v-btn v-if="block.slotsFilled > 0" class="mt-4" @click="filter.duty = [block._id]; blockDialog = false; tab = 1;">View {{ block.slotsFilled }} Student{{ block.slotsFilled === 1 ? '' : 's' }} Who Chose This</v-btn>
                </v-card-text>
                <v-card-actions>
                  <v-btn @click="saveBlock()">{{ block._id ? 'Update' : 'Add' }} Option</v-btn>
                  <v-spacer />
                  <v-btn @click="blockDialog = false">Cancel</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-row>
        </v-card-text>
      </v-tab-item>
      <v-tab-item>
        <v-toolbar>
          <v-text-field v-model="filter.name" label="Name Filter" dense hide-details outlined class="mr-2" style="max-width:250px" />
          <v-select v-model="filter.status" :items="statusOptions" label="Status Filter" dense hide-details multiple outlined class="mr-2" style="max-width:250px" />
          <v-select v-model="filter.duty" :items="blocks" label="Duty/Option Filter" item-text="desc" item-value="_id" dense hide-details multiple outlined class="mr-2" style="max-width:350px" />
          <v-spacer />
          <email-dialog :data="filteredStudents.filter(({ _id }) => selectedStudents.includes(_id))">
            <template #activator="{ on }">
              <v-btn v-on="on" :disabled="selectedStudents.length === 0">
                <v-icon left>fal fa-envelope</v-icon>
                Email
              </v-btn>
            </template>
          </email-dialog>
          <v-dialog v-model="editBatchDialog" width="600">
            <template #activator="{ on }">
              <v-btn v-on="on" :disabled="selectedStudents.length === 0" class="ml-2">
                <v-icon left>fal fa-pencil</v-icon>
                Edit
              </v-btn>
            </template>
            <v-card>
              <v-card-title>Edit Selected Students</v-card-title>
              <v-card-text>
                <p>Here you can edit the students that were selected in the table. You can change the status for the selected students, or you can add/remove duties from them, or both.</p>
                <v-select v-model="batchStatus" :items="batchStatusOptions" label="Change Status To" outlined />
                <v-select v-model="batchBlockAction" :items="batchBlockActionOptions" label="Duty/Option Change" outlined />
                <v-autocomplete v-if="batchBlockAction" v-model="batchChangeBlock" :items="blocks.map(({ _id: value, desc: text }) => { return { text, value } })" :label="`Duty/Option to ${batchBlockAction}`" outlined hide-details />
                <v-checkbox v-model="batchNotifyStudents" label="Email Students" />
                <v-text-field v-if="batchNotifyStudents" v-model="batchNotifySubject" label="Subject" outlined hide-details />
                <html-editor v-if="batchNotifyStudents" v-model="batchNotifyBody" label="Email Body" />
              </v-card-text>
              <v-card-actions>
                <v-btn :disabled="!batchStatus && !batchBlockAction" text>Update Selected Students</v-btn>
                <v-spacer />
                <v-btn text @click="editBatchDialog = false">Cancel</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-toolbar>
        <v-data-table v-model="selectedStudents" :items="filteredStudents" :headers="headers" show-select>
          <template #item.status="{ item }">
            {{ item.status }}
            <v-tooltip top>
              <template #activator="{ on: tooltipOn }">
                <v-menu>
                  <template #activator="{ on: menuOn }">
                    <v-btn v-on="{ ...tooltipOn, ...menuOn }" icon>
                      <v-icon small>fal fa-edit</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item v-for="status in statusOptions" :key="status" @click="setStatus(status, item._id)">
                      <v-list-item-title>{{ status }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
              <span>Edit Status</span>
            </v-tooltip>
          </template>
          <template #item.blocks="{ item }">
            <v-badge v-for="{ blockId, desc, hours } of item.blocks" :key="blockId" :content="hours + ' Hr' + (hours === 1 ? '' : 's')" offset-x="30" offset-y="8" overlap class="mr-2">
              <v-chip outlined label>{{ desc }} ({{ hours }} Hour{{ hours === 1 ? '' : 's' }})</v-chip>
            </v-badge>
          </template>
        </v-data-table>
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>
<script>
import { ref, computed, watch, onMounted } from '@vue/composition-api'

export default {
  components: {
    DatePicker: () => import('@/components/forms/inputs/DatePicker'),
    EmailDialog: () => import('@/components/system/emailDialog'),
    HtmlEditor: () => import('@/components/forms/HTMLEditor')
  },
  setup (props, { root }) {
    const id = computed(() => root.$route.params.id)
    const term = ref('')
    const isLoaded = ref(false)
    const eventTitle = ref('')
    const eventDate = ref('')
    const tab = ref(0)
    const description = ref('')
    const regOpen = ref('')
    const regClosed = ref('')
    const statusOptions = ref(['Committed', 'Cancelled', 'Completed', 'Excused', 'No Show'])
    const students = ref([])

    watch([description, regOpen, regClosed], async () => {
      if (isLoaded.value) {
        await root.$feathers.service('serve/event').patch(id.value, { description: description.value, regOpen: regOpen.value, regClosed: regClosed.value })
      }
    })

    const commitedCompleteStudents = computed(() => students.value.filter(({ status }) => status === 'Committed' || status === 'Completed').length)

    const isDeleting = ref(false)
    const deleteDialog = ref(false)
    async function deleteEvent () {
      isDeleting.value = true
      await root.$feathers.service('serve/event').patch(id.value, { active: false })
      // Remove any reference to this event from the students who signed up
      for (const { pidm } of students.value) {
        const { data } = await root.$feathers.service('serve/student').find({ query: { term: term.value, pidm } })
        if (data.length > 0) {
          await root.$feathers.service('serve/student').patch(data[0]._id, { $pull: { history: { event: id.value } } })
        }
      }
      isDeleting.value = false
      root.$store.dispatch('main/snackbar', { type: 'success', text: 'Event has been successfully deleted.' })
      root.$router.push('/serve/super')
    }

    const blockDialog = ref(false)
    const blockSearch = ref('')
    const blockFilter = ref('')
    const blockFilterItems = ref([
      { text: 'All Options', value: '' },
      { text: 'All Slots Empty', value: 'empty' },
      { text: 'Slots Partially Filled', value: 'under' },
      { text: 'Slots Fully Filled', value: 'filled' },
      { text: 'Slots Overfilled', value: 'over' }
    ])
    const blocks = ref([])
    const filteredBlocks = computed(() => blocks.value.filter(({ desc, date, slotCount, slotsFilled }) => {
      if (blockSearch.value) {
        const regex = new RegExp(blockSearch.value)
        if (!regex.test(desc + ' ' + date + ' ' + date.toDateString())) return false
      }
      if (blockFilter.value === 'filled') return slotsFilled === slotCount
      else if (blockFilter.value === 'over') return slotsFilled > slotCount
      else if (blockFilter.value === 'empty') return slotsFilled === 0
      else if (blockFilter.value === 'under') return slotsFilled < slotCount
      return true
    }))
    const block = ref({
      desc: '',
      slotCount: 0,
      minHours: 0,
      maxHours: 10
    })
    function resetBlock () {
      block.value = {
        desc: '',
        slotCount: 0,
        minHours: 0,
        maxHours: 10
      }
    }
    function addBlock () {
      resetBlock()
      blockDialog.value = true
    }
    function editBlock (id) {
      const rec = blocks.value.find(({ _id }) => _id === id)
      if (rec) {
        block.value = { ...rec }
        blockDialog.value = true
      }
    }
    async function saveBlock () {
      if (block.value._id) {
        const patch = {
          'blocks.$[el].desc': block.value.desc,
          'blocks.$[el].slotCount': block.value.slotCount,
          'blocks.$[el].minHours': block.value.minHours,
          'blocks.$[el].maxHours': block.value.maxHours
        }
        const arrayFilters = [{ 'el._id': block.value._id }]
        const res = await root.$feathers.service('serve/event').patch(id.value, patch, { query: { arrayFilters } })
        parseEvent(res)
      } else {
        const res = await root.$feathers.service('serve/event').patch(id.value, { $push: { blocks: block.value } })
        parseEvent(res)
      }
      blockDialog.value = false
      resetBlock()
    }
    async function removeBlock () {
      if (block.value.slotsFilled > 0) {
        if (confirm('Are you sure you want to remove this block? This will remove this option from the students who requested it but leave any other options they chose intact.')) {
          const res = await root.$feathers.service('serve/event').patch(id.value, { $pull: { blocks: { _id: block.value._id } } })
          const students = res.students.filter(({ blocks }) => blocks.find(({ blockId }) => blockId === block.value._id))
          console.log(students)
          // Adjust the hours
          parseEvent(res)
        }
      } else {
        if (confirm('Are you sure you want to remove this block?')) {
          const res = await root.$feathers.service('serve/event').patch(id.value, { $pull: { 'blocks._id': block.value._id } })
          parseEvent(res)
        }
      }
    }
    const editBatchDialog = ref(false)
    const batchStatus = ref('')
    const batchStatusOptions = computed(() => {
      const arr = [ ...statusOptions.value ]
      arr.splice(0, 0, { text: 'No Change', value: '' })
      return arr
    })
    const batchBlockAction = ref('')
    const batchBlockActionOptions = ref([
      { text: 'No Change', value: '' },
      { text: 'Add Duty/Option', value: 'add' },
      { text: 'Remove Duty/Option', value: 'remove' }
    ])
    const batchChangeBlock = ref('')
    const batchNotifyStudents = ref(false)
    const batchNotifySubject = ref('')
    const batchNotifyBody = ref('')

    const filter = ref({
      status: [],
      duty: [],
      name: ''
    })
    const filteredStudents = computed(() => students.value.filter(({ first, last, status, blocks }) => {
      if (filter.value.status.length > 0 && !filter.value.status.includes(status)) return false
      if (filter.value.duty.length > 0 && (blocks.length === 0 || !blocks.find(({ blockId }) => filter.value.duty.includes(blockId)))) return false
      if (filter.value.name) {
        const regex = new RegExp(filter.value.name, 'ig')
        return regex.test(`${last}, ${first} ${last}`)
      }
      return true
    }))
    const headers = ref([
      { text: 'Last Name', value: 'last' },
      { text: 'First Name', value: 'first' },
      { text: 'Status', value: 'status' },
      { text: 'Hours', value: 'hours' },
      { text: 'Selected Duties/Options', value: 'blocks' }
    ])
    const selectedStudents = ref([])

    async function setStatus (status, stuId) {
      if (stuId) {
        const index = students.value.findIndex(({ _id }) => _id === stuId)
        if (index >= 0) {
          const res = await root.$feathers.service('serve/event').patch(id.value, { 'students.$[el].status': status }, { query: { arrayFilters: [{ 'el._id': stuId }] } })
          const pidm = students.value[index].pidm
          const { data } = await root.$feathers.service('serve/student').find({ query: { term: term.value, pidm } })
          for (const { _id, history } of data) {
            const completedHours = history.filter(({ status }) => status === 'Completed').reduce((prev, { hours }) => prev + hours, 0)
            await root.$feathers.service('serve/student').patch(_id, { 'history.$[el].status': status, hours: completedHours }, { query: { arrayFilters: [{ 'el.event': id.value }] } })
          }
          parseEvent(res)
        }
      } else {
        const pidms = selectedStudents.value.map(({ pidm }) => pidm)
        // Update the records for these students in the event
        const res = await root.$feathers.service('serve/event').patch(id.value, { 'students.$[el].status': status }, { query: { arrayFilters: [{ 'el.pidm': { $in: pidms } }] } })
        // Update the records for these students in their student serve record
        const { data } = await root.$feathers.service('serve/student').find({ query: { term: term.value, pidm: { $in: pidms } } })
        for (const { _id, history } of data) {
          const completedHours = history.filter(({ status }) => status === 'Completed').reduce((prev, { hours }) => prev + hours, 0)
          await root.$feathers.service('serve/student').patch(_id, { 'history.$[el].status': status, hours: completedHours }, { query: { arrayFilters: [{ 'el.event': id.value }] } })
        }
        parseEvent(res)
      }
    }

    onMounted(async () => {
      const rec = await root.$feathers.service('serve/event').get(id.value)
      term.value = rec.term
      eventTitle.value = rec.name
      eventDate.value = rec.date
      description.value = rec.description
      regOpen.value = rec.regOpen
      regClosed.value = rec.regClosed
      await parseEvent(rec)
      isLoaded.value = true
    })

    async function parseEvent (rec) {
      if (rec.blocks) {
        blocks.value = rec.blocks.map((row) => {
          const slotsFilled = rec.students.filter(({ status, blocks }) => ['Committed', 'Completed'].includes(status) && !!blocks.find(({ blockId }) => blockId === row._id)).length
          return { ...row, slotsFilled }
        })
      }
      if (rec.students) {
        const pidms = rec.students.map(({ pidm }) => pidm)
        const { data: dirData } = await root.$feathers.service('directory/people').find({ query: { pidm: { $in: pidms }, $select: ['email', 'bannerId'] } })
        students.value = rec.students.map((row) => {
          row.id = row._id
          if (!row.hours) {
            row.hours = row.blocks.reduce((prev, { hours }) => prev + hours, 0)
          }
          const temp = dirData.find(({ pidm }) => pidm === row.pidm)
          if (temp) {
            row.email = temp.email
            row.bannerId = temp.bannerId
          }
          return row
        })
      }
    }

    return {
      id,
      eventTitle,
      eventDate,
      tab,
      description,
      regOpen,
      regClosed,
      isDeleting,
      deleteDialog,
      commitedCompleteStudents,
      deleteEvent,
      blockDialog,
      blockSearch,
      blockFilter,
      blockFilterItems,
      blocks,
      filteredBlocks,
      block,
      resetBlock,
      addBlock,
      editBlock,
      saveBlock,
      removeBlock,
      editBatchDialog,
      batchStatus,
      batchStatusOptions,
      batchBlockAction,
      batchBlockActionOptions,
      batchChangeBlock,
      batchNotifyStudents,
      batchNotifySubject,
      batchNotifyBody,
      filter,
      students,
      filteredStudents,
      headers,
      selectedStudents,
      statusOptions,
      setStatus
    }
  }
}
</script>
